import {apiClient} from './client'
import type {OrganizationSettings, UpdateSettingsResult} from './settings'
import type {PendingInvitation} from './users'

export type AdminOrgCounts = {
	users: number
	organizationPermits: number
	sprints: number
}

export type AdminOrgSummary = {
	id: string
	name: string
	slug: string
	createdAt: string
	updatedAt: string
	archivedAt: string | null
	_count: AdminOrgCounts
}

export type AdminOrgSprint = {
	sprintNumber: number
	status: 'ACTIVE' | 'DRAFT'
	plannedStartDate: string
	plannedEndDate: string
}

export type AdminOrgDetail = AdminOrgSummary & {
	settings: OrganizationSettings
	regions: string[]
	currentSprint: AdminOrgSprint | null
}

export type ProvisionOrgInput = {
	name: string
	slug: string
	permitStartDate: string
	regions?: string[]
	adminEmail?: string | null
}

export type ProvisionOrgResult = {
	org: {id: string; name: string; slug: string}
	resumed: boolean
	backfilled: number
	sprint: 'created' | 'already-exists'
	invitation:
		| {id: string; email: string; status: string}
		| {email: string; error: string}
		| null
}

export type PermitPreview = {
	total: number
	byRegion: Array<{region: string; count: number; tier: 1 | 2 | 3}>
}

export type AdminOrgUser = {
	id: string
	email: string | null
	firstName: string | null
	lastName: string | null
	role: string
	deactivatedAt: string | null
	avatarUrl: string | null
	externalId: string
}

export type BulkInviteOutcome = {
	email: string
	ok: boolean
	error?: string
}

export type InviteRole = 'USER' | 'ADMIN'

export async function getAdminOrganizations(includeArchived = false): Promise<{
	data: AdminOrgSummary[]
}> {
	return apiClient(
		includeArchived
			? '/api/admin/organizations?includeArchived=true'
			: '/api/admin/organizations',
	)
}

export async function archiveAdminOrganization(
	orgId: string,
): Promise<{data: AdminOrgSummary}> {
	return apiClient(`/api/admin/organizations/${orgId}/archive`, {
		method: 'POST',
	})
}

export async function unarchiveAdminOrganization(
	orgId: string,
): Promise<{data: AdminOrgSummary}> {
	return apiClient(`/api/admin/organizations/${orgId}/unarchive`, {
		method: 'POST',
	})
}

export async function getAdminOrganization(
	orgId: string,
): Promise<{data: AdminOrgDetail}> {
	return apiClient(`/api/admin/organizations/${orgId}`)
}

export async function provisionOrganization(
	input: ProvisionOrgInput,
): Promise<{data: ProvisionOrgResult}> {
	return apiClient('/api/admin/organizations', {method: 'POST', body: input})
}

export async function updateAdminOrganization(
	orgId: string,
	data: {name?: string; slug?: string},
): Promise<{data: AdminOrgSummary}> {
	return apiClient(`/api/admin/organizations/${orgId}`, {
		method: 'PATCH',
		body: data,
	})
}

export async function updateAdminOrgSettings(
	orgId: string,
	data: Partial<
		Pick<
			OrganizationSettings,
			| 'staleThresholdDays'
			| 'weekStartDay'
			| 'sprintLengthWeeks'
			| 'permitStartDate'
			| 'autoCloseEnabled'
			| 'autoCloseMinuteOfDay'
		>
	>,
): Promise<{data: UpdateSettingsResult}> {
	return apiClient(`/api/admin/organizations/${orgId}/settings`, {
		method: 'PATCH',
		body: data,
	})
}

export async function deleteAdminOrganization(
	orgId: string,
	confirmSlug: string,
): Promise<{data: {deleted: boolean; slug: string}}> {
	return apiClient(`/api/admin/organizations/${orgId}`, {
		method: 'DELETE',
		body: {confirmSlug},
	})
}

export async function getAdminOrgUsers(
	orgId: string,
): Promise<{data: AdminOrgUser[]}> {
	return apiClient(`/api/admin/organizations/${orgId}/users`)
}

export async function getAdminOrgInvitations(
	orgId: string,
): Promise<{data: PendingInvitation[]}> {
	return apiClient(`/api/admin/organizations/${orgId}/invitations`)
}

export async function bulkInviteToOrg(
	orgId: string,
	invites: Array<{email: string; role: InviteRole}>,
): Promise<{data: BulkInviteOutcome[]}> {
	return apiClient(`/api/admin/organizations/${orgId}/invitations`, {
		method: 'POST',
		body: {invites},
	})
}

export async function resendAdminOrgInvitation(
	orgId: string,
	invitationId: string,
): Promise<void> {
	await apiClient(
		`/api/admin/organizations/${orgId}/invitations/${invitationId}/resend`,
		{method: 'POST'},
	)
}

export async function revokeAdminOrgInvitation(
	orgId: string,
	invitationId: string,
): Promise<void> {
	await apiClient(
		`/api/admin/organizations/${orgId}/invitations/${invitationId}`,
		{method: 'DELETE'},
	)
}

export async function updateAdminOrgRegions(
	orgId: string,
	regions: string[],
	backfill: boolean,
): Promise<{data: {regions: string[]; backfilled: number}}> {
	return apiClient(`/api/admin/organizations/${orgId}/regions`, {
		method: 'PUT',
		body: {regions, backfill},
	})
}

export async function getPermitPreview(
	from: string,
): Promise<{data: PermitPreview}> {
	return apiClient(`/api/admin/permit-preview?from=${from}`)
}

// --- Staff (org-less DEVELOPER) management --------------------------------

export type StaffUser = AdminOrgUser

export async function getStaff(): Promise<{data: StaffUser[]}> {
	return apiClient('/api/admin/staff')
}

export async function getStaffInvitations(): Promise<{
	data: PendingInvitation[]
}> {
	return apiClient('/api/admin/staff/invitations')
}

export async function inviteStaff(
	email: string,
): Promise<{data: {id: string; email_address: string; status: string}}> {
	return apiClient('/api/admin/staff/invitations', {
		method: 'POST',
		body: {email},
	})
}

export async function resendStaffInvitation(
	invitationId: string,
): Promise<void> {
	await apiClient(`/api/admin/staff/invitations/${invitationId}/resend`, {
		method: 'POST',
	})
}

export async function revokeStaffInvitation(
	invitationId: string,
): Promise<void> {
	await apiClient(`/api/admin/staff/invitations/${invitationId}`, {
		method: 'DELETE',
	})
}

export async function deactivateStaff(userId: string): Promise<void> {
	await apiClient(`/api/admin/staff/${userId}/deactivate`, {method: 'PATCH'})
}

export async function reactivateStaff(userId: string): Promise<void> {
	await apiClient(`/api/admin/staff/${userId}/reactivate`, {method: 'PATCH'})
}
